import React, { useState, useEffect } from 'react';
import {
  Info, X, Check, ArrowRight, UserSwitch, ClockCounterClockwise, ArrowClockwise, WarningCircle,
} from '@phosphor-icons/react';
import {
  getBranchNames,
  getGroupCode,
  getTickets,
  getGuarantorNameChangeDetails,
  updateSubscriberGuarantorName,
  saveData,
} from '../api/connectionRegistryApi';
import SearchableSelect from './SearchableSelect';
import SectionLock from './SectionLock';
import { useLoginName, buildAuditEntry, apiErrorMessage } from '../utils/auditUser';
import Toast from './Toast';
import { useToast } from '../utils/useToast';
import { NO_DATA_MESSAGE, NO_DATA_SUMMARY, isEmptyResult } from '../utils/noData';

// Mirrors the Angular onNameInput(): strip anything that is not a letter or a
// space, then hard-truncate. Both name fields are sanitised as the user types,
// so the pattern/maxlength validators can never actually fire.
const sanitizeName = (value, maxLength) => (value || '')
  .replace(/[^A-Za-z ]/g, '')
  .substring(0, maxLength);

function GuarantorNameChangeForm() {
  const loginName = useLoginName();

  const [formKey, setFormKey] = useState(0);
  const [saving, setSaving] = useState(false);
  const { toast, setToast, showToast } = useToast();

  const [branchlist, setBranchlist] = useState([]);
  const [branchCodeList, setBranchCodeList] = useState([]); // groups
  const [tickets, setTickets] = useState([]);

  const [cao, setCao] = useState('');
  const [group, setGroup] = useState('');
  const [ticket, setTicket] = useState('');

  // Section B — read-only, loaded from the selected ticket
  const [oldName, setOldName] = useState('');
  const [oldSurname, setOldSurname] = useState('');
  const [oldMailingName, setOldMailingName] = useState('');
  const [subscriberName, setSubscriberName] = useState('');

  // Section C — editable
  const [newName, setNewName] = useState('');
  const [newSurname, setNewSurname] = useState('');

  const [reason, setReason] = useState('');

  // Mirrors Angular's per-control `touched` state: a field shows its error once
  // the user has interacted with it, or once Save has been pressed
  // (markAllAsTouched).
  const [touched, setTouched] = useState({});
  const markTouched = (field) => setTouched((prev) => ({ ...prev, [field]: true }));

  const [branchschema, setBranchschema] = useState(null);
  const [groupCode, setGroupCode] = useState(null);
  const [chitgroupId, setChitgroupId] = useState(null);
  const [contactId, setContactId] = useState(null);

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [sameNameWarning, setSameNameWarning] = useState(false);
  const [pendingAudit, setPendingAudit] = useState(null);
  const [pendingRaw, setPendingRaw] = useState(null);
  const [pendingReason, setPendingReason] = useState('');

  useEffect(() => {
    getBranchNames().then((res) => setBranchlist(res || [])).catch(() => setBranchlist([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formKey]);

  // Auto-generated, exactly as Angular's concatmailing() builds it.
  const newMailingName = `${newName.trim()} ${newSurname.trim()}`.trim();

  const resetFromTicket = () => {
    setContactId(null);
    setOldName('');
    setOldSurname('');
    setOldMailingName('');
    setSubscriberName('');
    setNewName('');
    setNewSurname('');
  };

  const resetFromGroup = () => {
    setGroupCode(null);
    setChitgroupId(null);
    setTicket('');
    setTickets([]);
    resetFromTicket();
  };

  const resetFromBranch = () => {
    setBranchschema(null);
    setGroup('');
    setBranchCodeList([]);
    resetFromGroup();
  };

  const handleBranchChange = async (branchName) => {
    setCao(branchName);
    markTouched('cao');
    resetFromBranch();
    if (!branchName) return;

    const selected = branchlist.find((x) => x.branch_name === branchName);
    if (!selected) return;

    const schema = selected.branch_code;
    setBranchschema(schema);

    try {
      const res = await getGroupCode(schema);
      setBranchCodeList(res || []);
      if (isEmptyResult(res)) showToast('warn', NO_DATA_SUMMARY, NO_DATA_MESSAGE);
    } catch {
      setBranchCodeList([]);
    }
  };

  const handleGroupChange = async (groupCodeValue) => {
    setGroup(groupCodeValue);
    markTouched('group');
    resetFromGroup();
    if (!groupCodeValue) return;

    const selected = branchCodeList.find((x) => x.group_Code === groupCodeValue);
    if (!selected) return;

    setGroupCode(selected.group_Code);
    setChitgroupId(Number(selected.chitgroup_id));

    try {
      const res = await getTickets(branchschema, selected.group_Code);
      setTickets(res || []);
      if (isEmptyResult(res)) showToast('warn', NO_DATA_SUMMARY, NO_DATA_MESSAGE);
    } catch {
      setTickets([]);
    }
  };

  const handleTicketChange = async (ticketno) => {
    setTicket(ticketno);
    markTouched('ticket');
    resetFromTicket();
    if (!ticketno) return;

    try {
      // GetnamechangeDetails returns an array — grab the first row.
      const res = await getGuarantorNameChangeDetails(branchschema, groupCode, ticketno, branchschema);
      const rows = Array.isArray(res) ? res : (res ? [res] : []);
      const data = rows.length ? rows[0] : null;
      // Nothing came back for this ticket — block with a popup so the empty
      // Current Details panel is never mistaken for real data.
      if (!data) {
        showToast('warn', 'No Data Found', `No guarantor details found for ticket no ${ticketno}.`, true);
        return;
      }

      setContactId(Number(data.tbl_mst_contact_id));
      setOldName(data.contact_name || '');
      setOldSurname(data.contact_surname || '');
      setOldMailingName(data.contact_mailing_name || '');
      setSubscriberName(data.subscriber_name || '');
    } catch (err) {
      console.error('Failed to fetch guarantor details:', err);
      showToast('error', 'Load Failed', apiErrorMessage(err, 'Failed to fetch guarantor details.'));
    }
  };

  const nameError = (() => {
    if (!newName.trim()) return 'required';
    if (newName.trim().length < 2) return 'minlength';
    return null;
  })();
  const surnameError = !newSurname.trim() ? 'required' : null;
  const reasonError = (() => {
    if (!reason.trim()) return 'required';
    if (reason.trim().length < 10) return 'minlength';
    return null;
  })();

  const caoInvalid = touched.cao && !cao;
  const groupInvalid = touched.group && !group;
  const ticketInvalid = touched.ticket && !ticket;
  const nameInvalid = touched.newName && !!nameError;
  const surnameInvalid = touched.newSurname && !!surnameError;
  const reasonInvalid = touched.reason && !!reasonError;

  const isValid = !!cao && !!group && !!ticket && !nameError && !surnameError && !reasonError;

  // ── Save flow ───────────────────────────────────────────────────
  // Step 1: validate + stage data, open confirm modal. The same-name check that
  // used to be its own dialog is folded in as a warning banner on this modal.
  const save = () => {
    setTouched({
      cao: true, group: true, ticket: true, newName: true, newSurname: true, reason: true,
    });

    if (!isValid || !contactId) {
      showToast('warn', 'Incomplete Form', 'Please fill all required fields correctly.');
      return;
    }

    const raw = {
      grtOldName: oldName,
      grtOldSurname: oldSurname,
      grtOldMailingName: oldMailingName,
      suscrName: subscriberName,
      grtNewName: newName.trim(),
      grtNewSurname: newSurname.trim(),
      grtNewMailingName: newMailingName,
      reason,
    };

    const newGuarantorName = raw.grtNewMailingName.trim().toLowerCase();
    const subscriber = (raw.suscrName || '').trim().toLowerCase();
    setSameNameWarning(!!(newGuarantorName && subscriber && newGuarantorName === subscriber));

    setPendingRaw(raw);
    setPendingReason(raw.reason);
    setPendingAudit(buildAuditEntry({
      schemaName: branchschema,
      loginName,
      changeType: 'GUARANTOR_NAME_CHANGE',
      oldData: {
        name: raw.grtOldName,
        surname: raw.grtOldSurname,
        mailingName: raw.grtOldMailingName,
      },
      newData: {
        name: raw.grtNewName,
        surname: raw.grtNewSurname,
        mailingName: raw.grtNewMailingName,
      },
      reason: raw.reason,
    }));

    setShowConfirmModal(true);
  };

  const cancelConfirm = () => {
    if (saving) return;
    setShowConfirmModal(false);
    setPendingAudit(null);
    setPendingRaw(null);
    setSameNameWarning(false);
  };

  // Step 2: fires only after the user clicks "Yes, Save" in the modal.
  const confirmSave = async () => {
    if (!pendingAudit || !pendingRaw) return;

    const raw = pendingRaw;
    setShowConfirmModal(false);
    setSaving(true);

    try {
      const res = await updateSubscriberGuarantorName(
        branchschema,
        chitgroupId,
        ticket,
        contactId,
        raw.grtNewName,
        raw.grtNewMailingName,
        raw.grtNewSurname,
      );

      // The backend returns plain text: "Success" or an error message.
      if (String(res ?? '').trim().toLowerCase() !== 'success') {
        setSaving(false);
        showToast('error', 'Update Failed', String(res || 'Update failed'), true);
        return;
      }
    } catch (err) {
      setSaving(false);
      console.error('Update failed:', err);
      showToast('error', 'Update Failed', apiErrorMessage(err, 'Update failed'), true);
      return;
    }

    try {
      await saveData([pendingAudit]);
      setSaving(false);
      showToast('success', 'Success', 'Successfully updated and audit saved', true);
      handleClear();
    } catch (err) {
      setSaving(false);
      console.error('Audit save failed:', err);
      showToast('warn', 'Partial Success', 'Updated, but audit failed', true);
    }
  };

  const handleClear = () => {
    setFormKey((k) => k + 1);
    setCao('');
    resetFromBranch();
    setReason('');
    setTouched({});
    setSameNameWarning(false);
    setPendingAudit(null);
    setPendingRaw(null);
  };

  return (
    <div className="nc-page">
      <Toast toast={toast} onClose={() => setToast(null)} testId="guarantor-name-change-toast" />

      <div style={{ background: 'white', border: '1px solid #E5E7EB', borderRadius: '8px', padding: '20px', position: 'relative' }} data-testid="guarantor-name-change-form">
        {saving && (
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '3px', background: '#002FA7', opacity: 0.6, borderRadius: '8px 8px 0 0' }} />
        )}

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{ width: '34px', height: '34px', borderRadius: '8px', background: '#EFF4FF', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <UserSwitch size={18} color="#002FA7" />
            </div>
            <div>
              <div style={{ fontSize: '11px', fontWeight: '600', color: '#002FA7', textTransform: 'uppercase' }}>Change Request</div>
              <div style={{ fontSize: '16px', fontWeight: '700', color: '#111827' }}>Guarantor Name Change</div>
            </div>
          </div>
          <span style={{ fontSize: '11px', fontWeight: '700', color: '#92400E', background: '#FFFBEB', border: '1px solid #FDE68A', padding: '4px 10px', borderRadius: '999px', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
            <ClockCounterClockwise size={12} /> In Progress
          </span>
        </div>

        {/* SECTION A: Select Ticket */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
          <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#002FA7', color: 'white', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>A</div>
          <div>
            <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>Select Ticket</div>
            <div style={{ fontSize: '11px', color: '#9CA3AF' }}>Choose branch, group and ticket to load guarantor details</div>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
          <div>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#6B7280', marginBottom: '4px' }}>
              CAO <span style={{ color: '#DC2626' }}>*</span>
            </label>
            <SearchableSelect
              key={`cao-${formKey}`}
              options={branchlist}
              getLabel={(o) => o.branch_name}
              getValue={(o) => o.branch_name}
              value={cao}
              onChange={handleBranchChange}
              placeholder="Select branch"
              emptyMessage="No branches found"
              error={caoInvalid}
              testId="guarantor-cao-select"
            />
            {caoInvalid && <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>Branch is required</div>}
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#6B7280', marginBottom: '4px' }}>
              Group Code <span style={{ color: '#DC2626' }}>*</span>
            </label>
            <SearchableSelect
              key={`group-${formKey}`}
              options={branchCodeList}
              getLabel={(o) => o.group_Code}
              getValue={(o) => o.group_Code}
              value={group}
              onChange={handleGroupChange}
              placeholder="Select group code"
              emptyMessage="Select a CAO first"
              disabled={!branchschema}
              error={groupInvalid}
              testId="guarantor-group-select"
            />
            {groupInvalid && <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>Group Code is required</div>}
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#6B7280', marginBottom: '4px' }}>
              Ticket No <span style={{ color: '#DC2626' }}>*</span>
            </label>
            <SearchableSelect
              key={`ticket-${formKey}`}
              options={tickets}
              getLabel={(o) => String(o.ticketno)}
              getValue={(o) => o.ticketno}
              value={ticket}
              onChange={handleTicketChange}
              placeholder="Select ticket no"
              emptyMessage="Select a group code first"
              disabled={!groupCode}
              error={ticketInvalid}
              testId="guarantor-ticket-select"
            />
            {ticketInvalid && <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>Ticket No is required</div>}
          </div>
        </div>

        <div style={{ height: '1px', background: '#E5E7EB', margin: '20px 0' }} />

        <SectionLock locked={!ticket} hint="Select a ticket in section A first">
        {/* SECTION B: Current Details */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
          <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#002FA7', color: 'white', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>B</div>
          <div>
            <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>Current Details</div>
            <div style={{ fontSize: '11px', color: '#9CA3AF' }}>Read-only details loaded from the selected ticket</div>
          </div>
        </div>

        <div style={{ border: '1px solid #E5E7EB', borderRadius: '8px', padding: '14px', background: '#F9FAFB' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', fontWeight: '600', color: '#6B7280', marginBottom: '10px' }}>
            <ClockCounterClockwise size={13} /> Current Details
            <span style={{ fontSize: '9px', fontWeight: '700', color: '#6B7280', background: '#F3F4F6', padding: '2px 6px', borderRadius: '4px' }}>READ ONLY</span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
            <div>
              <label style={{ display: 'block', fontSize: '11px', marginBottom: '2px' }}>Guarantor Old Name</label>
              <input className="input-field" style={{ fontSize: '13px', background: '#F9FAFB' }} value={oldName} readOnly data-testid="guarantor-old-name" />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '11px', marginBottom: '2px' }}>Guarantor Old Surname</label>
              <input className="input-field" style={{ fontSize: '13px', background: '#F9FAFB' }} value={oldSurname} readOnly data-testid="guarantor-old-surname" />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '11px', marginBottom: '2px' }}>Old Mailing Name</label>
              <input className="input-field" style={{ fontSize: '13px', background: '#F9FAFB' }} value={oldMailingName} readOnly data-testid="guarantor-old-mailing" />
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px', marginTop: '12px' }}>
            <div>
              <label style={{ display: 'block', fontSize: '11px', marginBottom: '2px' }}>Subscriber Name</label>
              <input className="input-field" style={{ fontSize: '13px', background: '#F9FAFB' }} value={subscriberName} readOnly data-testid="guarantor-subscriber-name" />
            </div>
          </div>
        </div>
        </SectionLock>

        <div style={{ height: '1px', background: '#E5E7EB', margin: '20px 0' }} />

        <SectionLock locked={!ticket} hint="Select a ticket in section A first">
        {/* SECTION C: New Guarantor Details */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
          <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#002FA7', color: 'white', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>C</div>
          <div>
            <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>New Guarantor Details</div>
            <div style={{ fontSize: '11px', color: '#9CA3AF' }}>Letters and spaces only — mailing name is generated automatically</div>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
          <div>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#6B7280', marginBottom: '4px' }}>
              Guarantor New Name <span style={{ color: '#DC2626' }}>*</span>
            </label>
            <input
              className="input-field"
              style={{ fontSize: '13px', borderColor: nameInvalid ? '#DC2626' : undefined }}
              value={newName}
              maxLength={50}
              placeholder="Enter new name"
              onChange={(e) => setNewName(sanitizeName(e.target.value, 50))}
              onBlur={() => markTouched('newName')}
              data-testid="guarantor-new-name"
            />
            {nameInvalid && (
              <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>
                {nameError === 'required' ? 'Guarantor name is required' : 'Minimum 2 characters required'}
              </div>
            )}
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#6B7280', marginBottom: '4px' }}>
              Guarantor New Surname <span style={{ color: '#DC2626' }}>*</span>
            </label>
            <input
              className="input-field"
              style={{ fontSize: '13px', borderColor: surnameInvalid ? '#DC2626' : undefined }}
              value={newSurname}
              maxLength={20}
              placeholder="Enter new surname"
              onChange={(e) => setNewSurname(sanitizeName(e.target.value, 20))}
              onBlur={() => markTouched('newSurname')}
              data-testid="guarantor-new-surname"
            />
            {surnameInvalid && (
              <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>Guarantor surname is required</div>
            )}
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#6B7280', marginBottom: '4px' }}>
              Guarantor New Mailing Name
            </label>
            <input className="input-field" style={{ fontSize: '13px', background: '#F9FAFB' }} value={newMailingName} readOnly data-testid="guarantor-new-mailing" />
          </div>
        </div>
        </SectionLock>

        <div style={{ height: '1px', background: '#E5E7EB', margin: '20px 0' }} />

        <SectionLock locked={!ticket || !!nameError || !!surnameError} hint="Enter a valid new name and surname in section C first">
        {/* SECTION D: Reason */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
          <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#002FA7', color: 'white', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>D</div>
          <div>
            <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>Reason for Change</div>
            <div style={{ fontSize: '11px', color: '#9CA3AF' }}>Minimum 10 characters — this will be recorded in the audit log</div>
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
          <label style={{ fontSize: '11px', fontWeight: '600', color: '#6B7280' }}>Reason <span style={{ color: '#DC2626' }}>*</span></label>
          <span style={{ fontSize: '11px', color: reason.trim().length >= 10 ? '#059669' : reason.length > 180 ? '#D97706' : '#9CA3AF' }}>
            {reason.length} / 200
          </span>
        </div>
        <textarea
          className="input-field"
          style={{ fontSize: '13px', borderColor: reasonInvalid ? '#DC2626' : undefined }}
          rows={4}
          maxLength={200}
          placeholder="Explain why this guarantor name change is being made..."
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          onBlur={() => markTouched('reason')}
          data-testid="guarantor-reason"
        />
        {reasonInvalid && (
          <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>
            {reasonError === 'required' ? 'Reason is required' : 'Reason must be at least 10 characters'}
          </div>
        )}
        </SectionLock>

        {/* ACTION BAR */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: '#9CA3AF' }}>
            <Info size={13} /> Fields marked with <strong>*</strong> are required
          </div>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button type="button" onClick={handleClear} className="btn-secondary" disabled={saving} data-testid="guarantor-clear-button">
              <ArrowClockwise size={14} style={{ marginRight: '4px' }} /> Clear
            </button>
            <button type="button" onClick={save} className="btn-primary" disabled={saving} data-testid="guarantor-save-button">
              <Check size={14} style={{ marginRight: '4px' }} /> {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>

      {/* Confirm modal (replaces the PrimeNG confirm dialog + same-name dialog) */}
      {showConfirmModal && (
        <div
          onClick={cancelConfirm}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(17,24,39,0.5)', display: 'flex',
            alignItems: 'center', justifyContent: 'center', zIndex: 50,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ background: 'white', borderRadius: '10px', width: '420px', maxWidth: '90vw', overflow: 'hidden' }}
            data-testid="guarantor-confirm-modal"
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid #E5E7EB' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <UserSwitch size={18} color="#002FA7" />
                <h5 style={{ margin: 0, fontSize: '15px', fontWeight: '700', color: '#111827' }}>Confirm Guarantor Name Change</h5>
              </div>
              <button type="button" onClick={cancelConfirm} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF' }}>
                <X size={16} />
              </button>
            </div>

            <div style={{ padding: '20px' }}>
              <p style={{ fontSize: '13px', color: '#6B7280', marginTop: 0 }}>
                You&apos;re about to update the guarantor name for this ticket. Please review before confirming.
              </p>

              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' }}>
                <div style={{ flex: 1 }}>
                  <p style={{ fontSize: '11px', color: '#9CA3AF', margin: 0 }}>Old Mailing Name</p>
                  <p style={{ fontSize: '13px', color: '#111827', margin: 0 }}>{pendingRaw?.grtOldMailingName || '—'}</p>
                </div>
                <ArrowRight size={14} color="#9CA3AF" />
                <div style={{ flex: 1 }}>
                  <p style={{ fontSize: '11px', color: '#9CA3AF', margin: 0 }}>New Mailing Name</p>
                  <p style={{ fontSize: '13px', color: '#059669', fontWeight: '600', margin: 0 }}>{pendingRaw?.grtNewMailingName}</p>
                </div>
              </div>

              {sameNameWarning && (
                <div style={{ display: 'flex', gap: '8px', fontSize: '12px', color: '#991B1B', background: '#FEF2F2', border: '1px solid #FECACA', padding: '10px', borderRadius: '6px', marginBottom: '10px' }} data-testid="guarantor-same-name-warning">
                  <WarningCircle size={14} style={{ flexShrink: 0, marginTop: '2px' }} />
                  <span>Both Subscriber and Guarantor names are not to be same. Requires Authority permission to proceed.</span>
                </div>
              )}

              <div style={{ display: 'flex', gap: '8px', fontSize: '12px', color: '#6B7280', background: '#F9FAFB', padding: '10px', borderRadius: '6px' }}>
                <Info size={14} style={{ flexShrink: 0, marginTop: '2px' }} />
                <span>This action will be recorded in the audit log with the reason: &quot;{pendingReason}&quot;</span>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', padding: '14px 20px', borderTop: '1px solid #E5E7EB' }}>
              <button type="button" onClick={cancelConfirm} disabled={saving} className="btn-secondary" data-testid="guarantor-confirm-cancel">
                Cancel
              </button>
              <button type="button" onClick={confirmSave} disabled={saving} className="btn-primary" data-testid="guarantor-confirm-save">
                <Check size={14} style={{ marginRight: '4px' }} /> Yes, Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default GuarantorNameChangeForm;
